import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Route-scoped error boundary.
 *
 * The app already has a top-level Sentry.ErrorBoundary in index.js that
 * unmounts the WHOLE app to the SentryFallback "Something broke" screen.
 * That's appropriate for a true white-screen, but is way too aggressive
 * for a per-tab render error (Bundle G regression: clicking Analytics
 * once would unmount the whole app; reload "fixed" it because the boundary
 * reset on route change).
 *
 * This boundary contains damage to a single route. If a component below
 * it throws during render, we show an inline "this section had a problem"
 * card with a Reload CTA. The error still goes to Sentry. The rest of
 * the app (top nav, sidebar, command palette) keeps working.
 *
 * Add a Sentry breadcrumb for each route so any future crash surfaced via
 * Sentry has accurate context about WHERE the user was when it blew up.
 */
function RouteFallback({ resetError, route }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--tm-bg-primary, #0a0a0a)", color: "var(--tm-text-primary, #fff)" }}
      data-testid={`route-error-${route}`}
    >
      <div className="max-w-md text-center">
        <h2 className="text-lg font-bold mb-2">This section hit a snag.</h2>
        <p className="text-sm opacity-70 mb-6">
          We logged the error. Reloading this page usually fixes it. The rest
          of the app should still work.
        </p>
        <div className="flex justify-center gap-2">
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: "var(--tm-accent, #00f0ff)", color: "var(--tm-bg-primary, #0a0a0a)" }}
            data-testid={`route-error-retry-${route}`}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.assign("/")}
            className="px-4 py-2 rounded-lg text-xs font-semibold border"
            style={{
              background: "transparent",
              color: "var(--tm-text-primary, #fff)",
              borderColor: "var(--tm-border-color, rgba(255,255,255,0.2))",
            }}
            data-testid={`route-error-home-${route}`}
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RouteErrorBoundary({ route, children }) {
  // Drop a breadcrumb on mount so any error that bubbles up has the route
  // context, even if React's component-stack is minified in production.
  React.useEffect(() => {
    try {
      Sentry.addBreadcrumb({
        category: "navigation.route",
        level: "info",
        message: `Mounted route: ${route}`,
      });
    } catch {
      // Sentry not initialised in some preview/test contexts — silent ignore.
    }
  }, [route]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <RouteFallback resetError={resetError} route={route} />}
      beforeCapture={(scope) => {
        scope.setTag("route", route);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
